import {z} from 'zod';
import {toast} from 'react-toastify';
import React, {FC, useState} from 'react';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsSpinner} from '@verdocs/web-sdk-react';
import {FormProvider, useForm} from 'react-hook-form';
import {IProfile, lockOrganizationMember, VerdocsEndpoint} from '@verdocs/js-sdk';
import {Button, TextInput} from '../Components';
import {Dialog} from './Dialog';

const formSchema = z.object({
  reason: z.string().trim().min(1, 'Please provide a reason.').max(255, 'Reason must be 255 characters or fewer.'),
});

type FormValues = z.infer<typeof formSchema>;

interface DialogProps {
  member: IProfile;
  onClose?: (locked?: boolean) => void;
}

export const LockMemberDialog: FC<DialogProps> = ({member, onClose = () => {}}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues: {reason: ''},
  });

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true);
    try {
      await lockOrganizationMember(VerdocsEndpoint.getDefault(), member.id, data.reason);
      toast.success('Member locked.');
      setSubmitting(false);
      onClose(true);
    } catch (e: any) {
      console.log('Error locking member', e);
      toast.error(e.response?.data?.error || 'Error locking member, please try again later.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog onCancel={() => onClose()}>
      <div className="mb-1 text-lg font-medium text-[#33364b]">
        Lock {[member.first_name, member.last_name].filter(Boolean).join(' ') || member.email}?
      </div>
      <div className="mb-4 text-[13px] leading-normal text-gray-500">
        They will be unable to sign in until an admin unlocks the account or they complete the password-reset flow.
      </div>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TextInput name="reason" label="Reason" required placeholder="e.g. Departed employee" />

          <div className="mt-5 flex flex-row items-center justify-end gap-2">
            {submitting && <VerdocsSpinner mode="dark" />}
            <Button type="button" size="small" variant="secondary" label="Cancel" onClick={() => onClose()} disabled={submitting} />
            <Button size="small" label="Lock account" type="submit" disabled={submitting || !form.formState.isValid} />
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};
